import { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Stack,
  Checkbox,
  Chip,
  IconButton,
  Avatar,
  InputBase,
  Divider,
  Card,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Circle as CircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material'
import { format, isToday, isYesterday } from 'date-fns'

export default function Inbox() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [user, setUser] = useState(null)
  const [messages, setMessages] = useState([])
  const [filteredMessages, setFilteredMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [selectedMessages, setSelectedMessages] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const [page, setPage] = useState(1)
  const pageSize = 15
  const [totalCount, setTotalCount] = useState(0)

  // Fetch the authenticated user
  useEffect(() => {
    const fetchUser = async () => {
      const { data: userData, error } = await supabase.auth.getUser()
      if (error) {
        console.error(t('inbox.errorFetchUser'), error.message)
        setLoading(false)
        return
      }
      setUser(userData?.user || null)
      setLoading(false)
    }
    fetchUser()
  }, [t])

  // Fetch messages when the user or page changes
  useEffect(() => {
    if (!user) return
    fetchMessages(user.id, page)
  }, [user, page])

  // Filter messages when search query changes
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMessages(messages)
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = messages.filter(
        (msg) =>
          msg.subject?.toLowerCase().includes(query) || msg.body?.toLowerCase().includes(query)
      )
      setFilteredMessages(filtered)
    }
  }, [searchQuery, messages])

  async function fetchMessages(userId, currentPage) {
    setLoading(true)
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize - 1

    const { data, error, count } = await supabase
      .from('messages')
      .select('id, subject, body, read, created_at', { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(start, end)

    if (error) {
      console.error(t('inbox.errorFetchMessages'), error.message)
      setLoading(false)
      return
    }
    setMessages(data || [])
    setFilteredMessages(data || [])
    setTotalCount(count || 0)
    setLoading(false)
  }

  const handleRefresh = async () => {
    if (!user) return
    setRefreshing(true)
    await fetchMessages(user.id, page)
    setRefreshing(false)
  }

  // When a row is clicked, set the selected message and mark it as read if needed
  const handleSelectMessage = (msg) => {
    setSelectedMessage(msg)
    if (!msg.read) {
      markAsRead(msg.id)
    }
  }

  async function markAsRead(messageId) {
    const { error } = await supabase.from('messages').update({ read: true }).eq('id', messageId)
    if (error) {
      console.error(t('inbox.errorMarkRead'), error.message)
      return
    }
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)))
    setFilteredMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, read: true } : m)))
  }

  async function markAllAsRead() {
    if (selectedMessages.length === 0) return

    const { error } = await supabase
      .from('messages')
      .update({ read: true })
      .in('id', selectedMessages)

    if (error) {
      console.error(t('inbox.errorMarkRead'), error.message)
      return
    }

    setMessages((prev) =>
      prev.map((m) => (selectedMessages.includes(m.id) ? { ...m, read: true } : m))
    )
    setFilteredMessages((prev) =>
      prev.map((m) => (selectedMessages.includes(m.id) ? { ...m, read: true } : m))
    )
  }

  async function handleDeleteSelectedMessages() {
    if (selectedMessages.length === 0) return

    const { error } = await supabase.from('messages').delete().in('id', selectedMessages)
    if (error) {
      console.error(t('inbox.errorDeleteMessages'), error.message)
      return
    }
    fetchMessages(user.id, page)
    setSelectedMessages([])
    setSelectAll(false)
  }

  // Toggle checkbox selection for an individual message
  const handleCheckboxChange = (messageId) => {
    setSelectedMessages((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    )
  }

  // Toggle select all checkboxes
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMessages([])
    } else {
      setSelectedMessages(filteredMessages.map((msg) => msg.id))
    }
    setSelectAll(!selectAll)
  }

  // Format date as relative
  const formatMessageDate = (dateString) => {
    const date = new Date(dateString)

    if (isToday(date)) {
      return format(date, 'HH:mm')
    } else if (isYesterday(date)) {
      return t('inbox.yesterday')
    } else if (date.getFullYear() === new Date().getFullYear()) {
      return format(date, 'MMM d')
    } else {
      return format(date, 'MM/dd/yyyy')
    }
  }

  // Get message initials for avatar
  const getMessageInitials = (subject) => {
    if (!subject) return '?'
    return subject
      .split(' ')
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase()
  }

  // Get random avatar color based on message ID
  const getAvatarColor = (messageId) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
    ]

    // Use the message ID to deterministically choose a color
    const colorIndex =
      messageId?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length
    return colors[colorIndex || 0]
  }

  // Render mobile message view
  const renderMobileView = () => {
    return (
      <Stack spacing={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
            <Typography color="text.secondary">
              {searchQuery ? t('inbox.noSearchResults') : t('inbox.noMessages')}
            </Typography>
          </Box>
        ) : (
          filteredMessages.map((message) => (
            <Card
              key={message.id}
              sx={{
                p: 2,
                cursor: 'pointer',
                backgroundColor: message.read
                  ? 'background.paper'
                  : alpha(theme.palette.primary.main, 0.05),
                '&:hover': {
                  boxShadow: 2,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease',
                },
              }}
              onClick={() => handleSelectMessage(message)}
            >
              <Box
                sx={{
                  display: 'flex',
                  mb: 1,
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: getAvatarColor(message.id),
                      width: 40,
                      height: 40,
                      fontSize: '0.9rem',
                    }}
                  >
                    {getMessageInitials(message.subject)}
                  </Avatar>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: message.read ? 'normal' : 'bold',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '180px',
                      }}
                    >
                      {message.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatMessageDate(message.created_at)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={selectedMessages.includes(message.id)}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCheckboxChange(message.id)
                    }}
                  />
                  {!message.read && (
                    <CircleIcon sx={{ color: theme.palette.primary.main, fontSize: 14 }} />
                  )}
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  ml: 6,
                }}
              >
                {message.body}
              </Typography>
            </Card>
          ))
        )}
      </Stack>
    )
  }

  // Render desktop table view
  const renderDesktopView = () => {
    return (
      <TableContainer component={Paper} elevation={3} sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedMessages.length > 0 && selectedMessages.length < filteredMessages.length
                  }
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell width="50%">{t('inbox.subject')}</TableCell>
              <TableCell width="30%">{t('inbox.date')}</TableCell>
              <TableCell width="20%">{t('inbox.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredMessages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <EmailIcon
                      sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 1 }}
                    />
                    <Typography color="text.secondary">
                      {searchQuery ? t('inbox.noSearchResults') : t('inbox.noMessages')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredMessages.map((message) => (
                <TableRow
                  key={message.id}
                  sx={{
                    backgroundColor: message.read
                      ? 'background.paper'
                      : alpha(theme.palette.primary.main, 0.05),
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                  onClick={() => handleSelectMessage(message)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedMessages.includes(message.id)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCheckboxChange(message.id)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(message.id),
                          width: 40,
                          height: 40,
                          fontSize: '0.9rem',
                        }}
                      >
                        {getMessageInitials(message.subject)}
                      </Avatar>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: message.read ? 'normal' : 'bold',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '400px',
                        }}
                      >
                        {message.subject}
                      </Typography>
                      {!message.read && (
                        <CircleIcon sx={{ color: theme.palette.primary.main, fontSize: 12 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(message.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={message.read ? t('inbox.read') : t('inbox.unread')}
                      size="small"
                      color={message.read ? 'default' : 'primary'}
                      variant={message.read ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: '1200px',
        mx: 'auto',
      }}
    >
      {/* Header with title and actions */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {t('inbox.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inbox.manageMessages')}
          </Typography>
        </Box>

        {/* Search and actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Paper
            component="form"
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: { xs: '100%', sm: 240 },
              borderRadius: 2,
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder={t('inbox.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              inputProps={{ 'aria-label': 'search messages' }}
            />
            <IconButton type="button" aria-label="search">
              <SearchIcon />
            </IconButton>
          </Paper>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('inbox.refresh')}>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                color="primary"
                sx={{
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Selected actions bar */}
      {selectedMessages.length > 0 && (
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            p: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2">
            {t('inbox.selectedCount', { count: selectedMessages.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MarkEmailReadIcon />}
              onClick={markAllAsRead}
            >
              {t('inbox.markRead')}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelectedMessages}
            >
              {t('inbox.delete')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Messages display - table on desktop, cards on mobile */}
      {isMobile ? renderMobileView() : renderDesktopView()}

      {/* Pagination */}
      {!loading && filteredMessages.length > 0 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
            siblingCount={isTablet ? 0 : 1}
          />
        </Stack>
      )}

      {/* Message Detail Dialog */}
      <Dialog
        open={Boolean(selectedMessage)}
        onClose={() => setSelectedMessage(null)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        {isMobile && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <IconButton edge="start" onClick={() => setSelectedMessage(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {selectedMessage?.subject}
            </Typography>
          </DialogTitle>
        )}
        {!isMobile && <DialogTitle>{selectedMessage?.subject}</DialogTitle>}
        <DialogContent dividers>
          <Box
            sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(selectedMessage?.id),
                  width: 48,
                  height: 48,
                }}
              >
                {getMessageInitials(selectedMessage?.subject)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {t('inbox.system')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedMessage && new Date(selectedMessage.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={
                selectedMessage?.read ? (
                  <CheckCircleOutlineIcon />
                ) : (
                  <CircleIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              label={selectedMessage?.read ? t('inbox.read') : t('inbox.unread')}
              color={selectedMessage?.read ? 'default' : 'primary'}
              variant={selectedMessage?.read ? 'outlined' : 'filled'}
              size="small"
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {selectedMessage?.body}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMessage(null)} color="primary">
            {t('inbox.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
